import { useCallback, useEffect, useMemo, useState } from 'react'
import { guaranies, fechaCorta, ZONA, diaLocal } from '../datos/formato.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { Boton } from '../comunes/Boton.js'
import { DateTime } from 'luxon'

/**
 * Reportes de producción y pendientes (T09b).
 *
 * **Excepción declarada a "la UI lee sólo de Dexie" (§5.5).** Los reportes son
 * agregaciones que la API calcula sobre datos que no se replican al dispositivo:
 * producción por profesional, saldos pendientes cruzados con terceros. Pedirlos
 * online es la decisión correcta; no son trabajo de agenda ni se consultan sin
 * conexión.
 */

const API = '/api/reportes'

// ── Tipos ────────────────────────────────────────────────────────────────────

interface PeriodoDTO {
  desde: string
  hasta: string
}

interface FilaProduccion {
  recursoPersonaId: string
  profesional: string
  cantidad: number
  producido: number
  cobrado: number
  pendiente: number
}

interface ProduccionDTO {
  periodo: PeriodoDTO
  moneda: string
  profesionales: FilaProduccion[]
  total: number
}

interface FilaPendiente {
  cargoId: string
  citaId: string
  cliente: string
  telefono: string
  monto: number
  pagado: number
  saldo: number
  deudor: 'paciente' | 'tercero'
  desde: string
}

interface PendientesDTO {
  periodo: PeriodoDTO
  moneda: string
  pendientes: FilaPendiente[]
  totalPacientes: number
  totalTerceros: number
}

// ── Períodos predefinidos ────────────────────────────────────────────────────

type PeriodoId = 'este_mes' | 'mes_pasado' | 'ultimos_30'

interface OpcionPeriodo {
  id: PeriodoId
  nombre: string
  rango: () => { desde: string; hasta: string }
}

const PERIODOS: OpcionPeriodo[] = [
  {
    id: 'este_mes',
    nombre: 'Este mes',
    rango: () => {
      const ahora = DateTime.now().setZone(ZONA)
      return {
        desde: ahora.startOf('month').toUTC().toISO() ?? '',
        hasta: ahora.endOf('month').toUTC().toISO() ?? '',
      }
    },
  },
  {
    id: 'mes_pasado',
    nombre: 'Mes pasado',
    rango: () => {
      const inicio = DateTime.now().setZone(ZONA).minus({ months: 1 }).startOf('month')
      return {
        desde: inicio.toUTC().toISO() ?? '',
        hasta: inicio.endOf('month').toUTC().toISO() ?? '',
      }
    },
  },
  {
    id: 'ultimos_30',
    nombre: 'Últimos 30 días',
    rango: () => {
      const ahora = DateTime.now().setZone(ZONA)
      return {
        desde: ahora.minus({ days: 30 }).startOf('day').toUTC().toISO() ?? '',
        hasta: ahora.endOf('day').toUTC().toISO() ?? '',
      }
    },
  },
]

// ── Componente principal ─────────────────────────────────────────────────────

export interface ReportesProps {
  onCobrar?: ((citaId: string, dia: string) => void) | undefined
}

export function Reportes({ onCobrar }: ReportesProps) {
  const [periodoId, setPeriodoId] = useState<PeriodoId>('este_mes')
  const [produccion, setProduccion] = useState<ProduccionDTO | null>(null)
  const [pendientes, setPendientes] = useState<PendientesDTO | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const rango = useMemo(
    () => PERIODOS.find((p) => p.id === periodoId)!.rango(),
    [periodoId],
  )

  const cargar = useCallback(async (desde: string, hasta: string) => {
    setCargando(true)
    setError(null)
    try {
      const params = new URLSearchParams({ desde, hasta })
      const [resProd, resPend] = await Promise.all([
        fetch(`${API}/produccion?${params}`, { credentials: 'include' }),
        fetch(`${API}/pendientes?${params}`, { credentials: 'include' }),
      ])
      if (!resProd.ok || !resPend.ok) throw new Error('No pudimos cargar los reportes')
      setProduccion((await resProd.json()) as ProduccionDTO)
      setPendientes((await resPend.json()) as PendientesDTO)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo salió mal')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar(rango.desde, rango.hasta)
  }, [cargar, rango])

  // ── Derivados de pendientes ──────────────────────────────────────────────

  const pendientesPaciente = useMemo(
    () => (pendientes?.pendientes ?? []).filter((p) => p.deudor === 'paciente'),
    [pendientes],
  )
  const pendientesTercero = useMemo(
    () => (pendientes?.pendientes ?? []).filter((p) => p.deudor === 'tercero'),
    [pendientes],
  )

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <TituloPagina
        titulo="Reportes"
        bajada="Producción y pendientes del período seleccionado."
        acciones={
          <select
            value={periodoId}
            onChange={(e) => setPeriodoId(e.target.value as PeriodoId)}
            className="h-9 rounded-lg border border-slate-300 px-3 text-sm text-slate-700"
          >
            {PERIODOS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        }
      />

      {error && (
        <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {cargando && (
        <p className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Cargando reportes…
        </p>
      )}

      {!cargando && !error && produccion && pendientes && (
        <>
          {/* ── Producción por profesional ──────────────────────────────── */}
          <section className="mb-5">
            <h3 className="mb-2 text-sm font-medium text-slate-700">
              Producción por profesional
            </h3>

            {produccion.profesionales.length === 0 ? (
              <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
                No hay producción registrada en este período.
              </p>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-medium">Profesional</th>
                      <th className="px-3 py-2 text-right font-medium">Cargos</th>
                      <th className="px-3 py-2 text-right font-medium">Producido</th>
                      <th className="px-3 py-2 text-right font-medium">Cobrado</th>
                      <th className="px-3 py-2 text-right font-medium">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...produccion.profesionales]
                      .sort((a, b) => b.producido - a.producido)
                      .map((prof) => (
                        <tr key={prof.recursoPersonaId} className="border-b border-slate-50">
                          <td className="px-3 py-2 text-slate-800">{prof.profesional}</td>
                          <td className="px-3 py-2 num text-slate-600">
                            {prof.cantidad}
                          </td>
                          <td className="px-3 py-2 num text-slate-800">
                            {guaranies(prof.producido)}
                          </td>
                          <td className="px-3 py-2 num text-teal-800">
                            {guaranies(prof.cobrado)}
                          </td>
                          <td className="px-3 py-2 num text-amber-700">
                            {guaranies(prof.pendiente)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 font-medium">
                      <td className="px-3 py-2 text-slate-800">Total</td>
                      <td className="px-3 py-2 num text-slate-600">
                        {produccion.profesionales.reduce((a, p) => a + p.cantidad, 0)}
                      </td>
                      <td className="px-3 py-2 num text-slate-800">
                        {guaranies(produccion.total)}
                      </td>
                      <td className="px-3 py-2 num text-teal-800">
                        {guaranies(produccion.profesionales.reduce((a, p) => a + p.cobrado, 0))}
                      </td>
                      <td className="px-3 py-2 num text-amber-700">
                        {guaranies(produccion.profesionales.reduce((a, p) => a + p.pendiente, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          {/* ── Pendientes de pacientes ─────────────────────────────────── */}
          <BloquePendientes
            titulo="Lo que deben los pacientes"
            ayuda="Se reclama en el mostrador."
            filas={pendientesPaciente}
            total={pendientes.totalPacientes}
            onCobrar={onCobrar}
          />

          {/* ── Pendientes de seguros ──────────────────────────────────── */}
          <BloquePendientes
            titulo="Lo que deben los seguros"
            ayuda="Se reclama con una factura y un teléfono."
            filas={pendientesTercero}
            total={pendientes.totalTerceros}
            onCobrar={onCobrar}
          />
        </>
      )}
    </div>
  )
}

// ── Bloque de pendientes (paciente o tercero) ────────────────────────────────

function BloquePendientes({
  titulo,
  ayuda,
  filas,
  total,
  onCobrar,
}: {
  titulo: string
  ayuda: string
  filas: FilaPendiente[]
  total: number
  onCobrar?: ((citaId: string, dia: string) => void) | undefined
}) {
  return (
    <section className="mb-5">
      <h3 className="mb-1 text-sm font-medium text-slate-700">{titulo}</h3>
      <p className="mb-2 text-xs text-slate-500">{ayuda}</p>

      {filas.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
          Sin pendientes en este período.
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Teléfono</th>
                <th className="px-3 py-2 text-right font-medium">Saldo</th>
                <th className="px-3 py-2 font-medium">Desde</th>
                {onCobrar && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.cargoId} className="border-b border-slate-50">
                  <td className="px-3 py-2 text-slate-800">{f.cliente}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{f.telefono || '—'}</td>
                  <td className="px-3 py-2 num text-amber-700">
                    {guaranies(f.saldo)}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {f.desde ? fechaCorta(f.desde) : '—'}
                  </td>
                  {onCobrar && (
                    <td className="px-3 py-2 text-right">
                      {/* El día que se manda es el del cargo, no el de la cita:
                          es una pista, no la verdad. `navegar` prefiere el día
                          que sale de la cita en Dexie y usa este sólo cuando no
                          la tiene, que es el caso normal acá —los pendientes
                          pueden ser de meses atrás, fuera de la ventana que el
                          canal operativo replica. */}
                      <Boton
                        tamano="sm"
                        variante="ghost"
                        onClick={() => onCobrar(f.citaId, diaLocal(f.desde))}
                      >
                        Cobrar
                      </Boton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 font-medium">
                <td colSpan={2} className="px-3 py-2 text-slate-800">
                  Total
                </td>
                <td className="px-3 py-2 num text-amber-700">
                  {guaranies(total)}
                </td>
                <td />
                {onCobrar && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  )
}
